import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import {Button, Checkbox, Icon} from '../../../sharedComponents/components'
import {Input} from '../../../sharedComponents/components/molecules/Input'
import {useTranslation} from 'react-i18next'

import PerformTaskForm from './PerformTaskForm'
import {EventStatusCode, PerformableTask, Subtask} from '../../../state/performTask/state'
import PerformTaskTitle from './PerformTaskTitle'
import {FormContainer} from './PerformCoolerCoolingTask'
import IsUserHealthInspector from '../../../hooks/userIsHealthInspector'

interface Props {
  task: PerformableTask
  setValue: any
  onAssetSaved: () => void
  onAssetRemoved: (assetId: string) => void
  payload: any
  handleUpdate: () => void
  handleRemove: () => void
  handleSubmit: () => void
  disabled?: boolean
  hasCurrentEvent?: boolean
  currentEventId?: string
}

interface SubtasksProps {
  tasks?: {name: string; options: string[]}[]
  onSelect?: (key: 'doneSubtasks' | 'selectedSubtaskOptions' | 'subtaskComments', selected: any) => void
  savedSubtasks?: string[]
  savedSubtaskOptions?: {subtaskName: string; option: string}[]
  savedSubtaskComments?: {subtaskName: string; comment: string}[]
  savedSubTasksWithActors?: Subtask[]
}

const SubtasksWidget = ({
  tasks,
  onSelect,
  savedSubtasks,
  savedSubtaskOptions,
  savedSubtaskComments,
  savedSubTasksWithActors
}: SubtasksProps) => {
  const [doneSubtasks, setDoneSubtasks] = useState<string[]>(savedSubtasks ? savedSubtasks : [])

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(
    Object.fromEntries((savedSubtaskOptions || []).map(selection => [selection.subtaskName, selection.option]))
  )

  const [expandedSubtasks, setExpandedSubtasks] = useState<Record<string, boolean>>({})
  const [subtaskComments, setSubtaskComments] = useState<Record<string, string>>(
    Object.fromEntries((savedSubtaskComments || []).map(item => [item.subtaskName, item.comment]))
  )

  const {t} = useTranslation('tasks')

  const taskNames = tasks?.map(task => task.name) || []

  const handleClick = (index: number) => {
    const clicked = taskNames[index]
    setDoneSubtasks(current =>
      current.includes(clicked) ? current.filter(task => task !== clicked) : [...current, clicked]
    )
  }

  const handleSelectAll = () => {
    setDoneSubtasks(current => (current.length === taskNames.length ? [] : taskNames))
  }

  const handleSelectOption = (subtaskName: string, option: string) => {
    setSelectedOptions(current => ({
      ...current,
      [subtaskName]: option
    }))
    onSelect?.('selectedSubtaskOptions', [
      ...Object.entries(selectedOptions)
        .filter(([name]) => name !== subtaskName)
        .map(([name, selected]) => ({subtaskName: name, option: selected})),
      {subtaskName, option}
    ])
  }

  const allSubtasksExpanded = taskNames.length > 0 && taskNames.every(name => expandedSubtasks[name])

  const handleToggleAll = () => {
    setExpandedSubtasks(Object.fromEntries(taskNames.map(name => [name, !allSubtasksExpanded])))
  }

  const checkActor = (taskName: string) => {
    if (savedSubtasks && savedSubtasks.includes(taskName)) {
      if (savedSubTasksWithActors) {
        for (const subtask of savedSubTasksWithActors) {
          if (subtask.subtaskName === taskName && subtask.completedBy) {
            return `${taskName} - ${subtask.completedBy}`
          }
        }

        return `${taskName} - N/A`
      }
    }

    return taskName
  }

  useEffect(() => {
    if (onSelect) {
      onSelect('doneSubtasks', doneSubtasks)
    }
  }, [doneSubtasks, onSelect])

  useEffect(() => {
    if (onSelect) {
      const selectedSubtaskOptions = Object.entries(selectedOptions).map(([subtaskName, option]) => ({
        subtaskName,
        option
      }))

      onSelect('selectedSubtaskOptions', selectedSubtaskOptions)
    }
  }, [selectedOptions, onSelect])

  useEffect(() => {
    if (onSelect) {
      const comments = Object.entries(subtaskComments)
        .filter(([, comment]) => comment.trim().length > 0)
        .map(([subtaskName, comment]) => ({subtaskName, comment}))
      onSelect('subtaskComments', comments)
    }
  }, [subtaskComments, onSelect])

  return tasks && tasks.length ? (
    <SubtasksContainer>
      <SelectAllWrapper>
        <ControlsRow>
          <Checkbox
            data-cy="perform-todo-checkbox-select-all"
            label={t('tasks:todoTask.selectAll', 'Select All')}
            name={t('tasks:todoTask.selectAll', 'Select All')}
            checked={doneSubtasks.length === tasks.length}
            onClick={handleSelectAll}
          />
          <ToggleAllButton type="button" onClick={handleToggleAll}>
            {allSubtasksExpanded
              ? t('tasks:todoTask.collapseAll', 'Collapse all')
              : t('tasks:todoTask.expandAll', 'Expand all')}
          </ToggleAllButton>
        </ControlsRow>
      </SelectAllWrapper>

      <Subtasks>
        {taskNames.map((taskName, index) => {
          const task = tasks[index]
          const isExpanded = Boolean(expandedSubtasks[taskName])
          const selectedOption = selectedOptions[taskName]

          return (
            <SubtaskWrapper key={index + taskName}>
              <SubtaskHeader>
                <SubtaskCheckbox>
                  <Checkbox
                    checked={doneSubtasks.includes(taskName)}
                    label={checkActor(taskName)}
                    name={taskName}
                    onClick={() => handleClick(index)}
                  />
                </SubtaskCheckbox>
                <SubtaskToggleButton
                  type="button"
                  onClick={() =>
                    setExpandedSubtasks(current => ({
                      ...current,
                      [taskName]: !current[taskName]
                    }))
                  }
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} options for ${taskName}`}
                >
                  <Icon type={isExpanded ? 'arrowhead-up-small' : 'arrowhead-down-small'} />
                </SubtaskToggleButton>
              </SubtaskHeader>

              {isExpanded && (
                <SubtaskOptions>
                  {task.options?.map(option => {
                    const isSelected = selectedOption === option

                    return (
                      <SubtaskOption
                        key={option}
                        selected={isSelected}
                        onClick={() => handleSelectOption(taskName, option)}
                        role="radio"
                        aria-checked={isSelected}
                      >
                        <RadioCircle selected={isSelected}>{isSelected && <RadioDot />}</RadioCircle>

                        <OptionText>{option}</OptionText>
                      </SubtaskOption>
                    )
                  })}
                  <Input
                    name={`subtask-comment-${taskName}`}
                    aria-label={`Comment for ${taskName}`}
                    placeholder={t('tasks:todoTask.subtaskComment', 'Add a comment for this subtask')}
                    value={subtaskComments[taskName] || ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      const comment = event.target.value
                      setSubtaskComments(current => ({
                        ...current,
                        [taskName]: comment
                      }))
                      onSelect?.('subtaskComments', [
                        ...Object.entries(subtaskComments)
                          .filter(([name, value]) => name !== taskName && value.trim().length > 0)
                          .map(([name, value]) => ({subtaskName: name, comment: value})),
                        ...(comment.trim().length > 0 ? [{subtaskName: taskName, comment}] : [])
                      ])
                    }}
                  />
                </SubtaskOptions>
              )}
            </SubtaskWrapper>
          )
        })}
      </Subtasks>
    </SubtasksContainer>
  ) : null
}

const SubtasksContainer = styled.div`
  padding: 1.5rem 0.5rem;
`

const SelectAllWrapper = styled.div`
  padding: 0 1rem;
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const Subtasks = styled.div`
  padding: 1rem 1.6rem;
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
`

const SubtaskWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.25rem 0;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
`

const SubtaskHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

const SubtaskCheckbox = styled.div`
  flex: 1;
`

const SubtaskToggleButton = styled.button`
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #000;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: monospace;
  line-height: 1;

  &:hover {
    background: #f5f5f5;
    border-radius: 50%;
  }

  &:focus {
    outline: 2px solid #1976d2;
    outline-offset: 1px;
  }
`

const ToggleAllButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  color: #000;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }

  &:focus {
    outline: 2px solid #1976d2;
    outline-offset: 1px;
  }
`

const SubtaskOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.25rem 0 0.25rem 2.5rem;
  padding: 0.25rem 0;
`

const SubtaskOption = styled.div<{selected: boolean}>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;

  background: ${({selected}) => (selected ? '#f0f7ff' : 'transparent')};

  &:hover {
    background: #f5f5f5;
  }
`

const RadioCircle = styled.div<{selected: boolean}>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 2px solid ${({selected}) => (selected ? '#1976d2' : '#999')};

  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`

const RadioDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1976d2;
`

const OptionText = styled.span`
  font-size: 0.85rem;
  color: #000;
`

const CompleteTaskButton = styled(Button)`
  margin: 1rem;
  width: 90%;
`

const PerformTodoTask = ({
  task,
  setValue,
  onAssetSaved,
  onAssetRemoved,
  payload,
  handleUpdate,
  handleSubmit,
  handleRemove,
  disabled,
  hasCurrentEvent,
  currentEventId
}: Props) => {
  const {t} = useTranslation('tasks')
  const {isHealthInspector} = IsUserHealthInspector()

  const handleCompleteTask = () => {
    payload.incompleteToDo = false

    if (task.status === 'in-progress') {
      handleUpdate()
    } else {
      handleSubmit()
    }
  }

  const disabledLocal = Boolean(disabled || isHealthInspector)

  return (
    <>
      <FormContainer>
        <PerformTaskTitle task={task} />

        <SubtasksWidget
          tasks={task.subtasks}
          onSelect={setValue}
          savedSubtasks={task.doneSubtasks}
          savedSubtaskOptions={task.selectedSubtaskOptions}
          savedSubtaskComments={task.subtaskComments}
          savedSubTasksWithActors={task.subTasksWithActors}
        />

        <PerformTaskForm
          onInputChange={setValue}
          payload={payload}
          setValue={setValue}
          onAssetSaved={onAssetSaved}
          onAssetRemoved={onAssetRemoved}
          handleRemove={task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS ? handleRemove : undefined}
          confirm={
            task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS
              ? t(
                  'tasks:labels.confirmRemoveTask',
                  'Are you sure you want to delete this task? This will also remove any associated resolved alarms.'
                )
              : undefined
          }
          disabled={disabledLocal}
          hasCurrentEvent={!!hasCurrentEvent}
          currentEventId={currentEventId}
          alwaysShowRelatedSites
        />
      </FormContainer>

      {task.subtasks && task.subtasks.length > 0 && (
        <CompleteTaskButton
          buttonStyle="primary"
          buttonSize="small"
          data-cy="complete-todo-task"
          data-testid="submit-button"
          onClick={handleCompleteTask}
          disabled={disabledLocal}
        >
          {t('tasks:actions.completeTask', 'Complete Task')}
        </CompleteTaskButton>
      )}
    </>
  )
}

export default PerformTodoTask
